/**
 * Image URL helpers — centralized place to treat URLs from the dead
 * AWS S3 bucket (`alph-md-storage.s3.*.amazonaws.com`) as "no URL"
 * so existing components fall through to their react-avatar / SVG
 * placeholder branches.
 *
 * KEEP THIS IN ONE PLACE so the day AWS comes back online (or we
 * migrate the data out of Mongo) we just clear DEAD_HOST_PATTERNS in
 * one file and every consuming surface heals automatically.
 */

const DEAD_HOST_PATTERNS = [
  /^https?:\/\/alph-md-storage\.s3\.[\w-]+\.amazonaws\.com/i,
];

/**
 * Placeholder paths the FDA import scripts store on products without a
 * real picture. No such files exist on the website, so they always 404.
 */
const PLACEHOLDER_IMAGE_PATTERNS = [
  /^\/images\/default-(drug|device)\.png$/i,
];

/**
 * Returns true when `url` is empty, null, or known to be hosted at a
 * dead host. Use this to decide whether to render an image at all.
 */
export const isDeadImageUrl = (url) => {
  if (!url || typeof url !== "string") return true;
  return [...DEAD_HOST_PATTERNS, ...PLACEHOLDER_IMAGE_PATTERNS].some((re) => re.test(url));
};

/**
 * Returns the URL if it's likely to load, otherwise `null`. Use this
 * at the read site so existing `{user?.profile ? <img/> : <Avatar/>}`
 * patterns fall through to Avatar.
 *
 *   const profileUrl = sanitizeImageUrl(user?.profile);
 *   return profileUrl ? <img src={profileUrl} /> : <Avatar … />;
 */
export const sanitizeImageUrl = (url) => (isDeadImageUrl(url) ? null : url);

/**
 * Convenience: pulls profile from any of the legacy field names and
 * returns the first one that's not from a dead host. Existing code
 * does this with `user?.profile || user?.profileImage || user?.logo`
 * which leaks dead URLs through — this version returns null when all
 * three are dead.
 */
export const firstValidProfileUrl = (user) => {
  if (!user) return null;
  return (
    sanitizeImageUrl(user.profile) ||
    sanitizeImageUrl(user.profileImage) ||
    sanitizeImageUrl(user.logo) ||
    null
  );
};
